import {BaseStage} from "./BaseStage";
import {loading, stopLoading} from "../../../helpers/loading";
import Axios from "axios";
import createElement from "../../../helpers/createElement";
import handleAjaxErrorV2 from "../../../helpers/handleAjaxErrorV2";
import {TotalPreviewComponent} from "../TotalPreviewComponent/TotalPreviewComponent";
import {getSingleInputByNameV3} from "../../../helpers/forms/formInputsHelpers";
import {applyForcedMinimumAddons} from "../ForcedMinimumAddons";
import {displayLocalValidationError} from "../../../helpers/forms/validationErrors/utils";
import {recoverSharedVisitorTypeUnavailable} from "../sharedInventory/sharedVisitorTypeUnavailable";

class TicketTypeSelectStage extends BaseStage {
    /** @var {TotalPreviewComponent} */
    totalPreviewComponent;

    constructor(globalState) {
        super(globalState);

        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)
        const shouldClearStateAtStartup = this.clearStateAtStartup();

        // Clear state
        if (shouldClearStateAtStartup) {
            this.globalState.setReserveTicketCounts([]);
            this.globalState.setGeneralAdmissionTicketCounts([]);
            this.globalState.setAddons([]);
            this.globalState.setDiscountCode('');
        }

        // Get route contents
        const getViewHtmlRoute = this.domContainer.dataset.getSeatTypesViewRoute;

        loading();

        const orderServerData = this.globalState.getOrderDataForServer();
        const isMixedComposition = this.isMixedCompositionEnabled();

        const orderData = {
            order_id: this.globalState.orderId,
            orderType: this.globalState.selectedSeatType,
            event_date_id: orderServerData.event_date_id,
        }

        // Preserve tracking data
        if (orderServerData.ad_tracking_keyword) {
            orderData.ad_tracking_keyword = orderServerData.ad_tracking_keyword;
            orderData.ad_tracking_session_public_id = orderServerData.ad_tracking_session_public_id;
        }

        // Append selected seats if required
        if (this.globalState.selectedSeatType === 'reserved') {
            orderData.reserved_seats = this.globalState.selectedSeats;
        }

        if (isMixedComposition) {
            orderData.mixed_order_composition = true;
            orderData.seats = orderServerData.seats;
            orderData.reserved_seats = orderServerData.reserved_seats;
            orderData.addons = orderServerData.addons;
            orderData.discounts = orderServerData.discounts;

            if (typeof this.globalState.getCompositionRequestDataForServer === 'function') {
                Object.assign(
                    orderData,
                    this.globalState.getCompositionRequestDataForServer()
                );
            }
        }

        // We append addons here, if they exists and are not empty
        // even though no addons should be set yet, this will help preserve
        // addon status for the admin order editor that inherits this constructor.
        // Required for the unfreeze button to work, as its behavior refresh this view
        // after clearing frozen flags, which means we must send back the updated addons
        // before the HTML is rendered again, to get the new addon.
        if (orderServerData.addons && orderServerData.addons.length) {
            orderData.addons = orderServerData.addons;
        }

        // Keep initial pending-order creation/update aligned with selected child dates.
        orderData.sub_orders = orderServerData.sub_orders;

        if (this.globalState.recoveringFromCapacityConflict) {
            orderData.recover_persisted_sub_orders = true;
            if (isMixedComposition) {
                orderData.render_only = true;
            }
            this.globalState.recoveringFromCapacityConflict = false;
        }

        if (!isMixedComposition && (shouldClearStateAtStartup || (orderServerData.seats && orderServerData.seats.length))) {
            orderData.seats = orderServerData.seats;
        }

        // Pass is express mode if defined, used by admin global state
        orderData.isExpressMode = orderServerData.isExpressMode;

        Axios.post(
            getViewHtmlRoute,
            orderData
        )
            .then(response => {
                // Retrieve HTML from server
                const viewHtml = createElement(response.data.view);

                // Replace contents from server
                this.domContainer.innerHTML = viewHtml.innerHTML;

                // Run default components initializations
                this.initSharedComponents();

                this.initView();
            })
            .catch(error => {
                if (this.recoverSharedVisitorTypeUnavailable(error)) {
                    return;
                }

                handleAjaxErrorV2(error)

                const ancillaryErrors = this.getAncillaryValidationErrors(error);

                if (ancillaryErrors.addons) {
                    this.globalState.setAddons([]);
                }

                if (ancillaryErrors.discount) {
                    this.globalState.setDiscountCode('');
                }

                this.globalState.stageManager.previousStage();
                this.globalState.stageManager.clearStageInstance('seat-type-select');
            })
            .finally(stopLoading)

        // Temporary hack
        // Handle iframe resizing and aspect ratio capping
        // noinspection DuplicatedCode
        window.addEventListener('message', (event) => {
            if (event.data && event.data.type === 'seat-map-dimensions') {
                const {width, height} = event.data;
                const iframe = this.domContainer.querySelector('.seat-map-iframe');
                const mapWrapper = iframe ? iframe.closest('.map-wrapper') : null;

                if (iframe && mapWrapper) {
                    const ratio = height / width;
                    const maxAxis = 400;

                    let targetWidth;
                    if (width >= height) {
                        targetWidth = maxAxis;
                    } else {
                        targetWidth = maxAxis / ratio;
                    }

                    // Cap by viewport width (minus a little padding) so it never overflows on tiny screens
                    const viewportWidth = Math.max(
                        document.documentElement?.clientWidth || 0,
                        window.innerWidth || 0
                    );
                    const maxAllowedWidth = Math.max(0, viewportWidth - 20);
                    if (targetWidth > maxAllowedWidth) {
                        targetWidth = maxAllowedWidth;
                    }

                    // Apply constraints to the wrapper
                    mapWrapper.style.width = `${targetWidth}px`;

                    const updateHeight = () => {
                        if (!iframe.isConnected) {
                            window.removeEventListener('resize', updateHeight);
                            return;
                        }
                        const currentWidth = iframe.offsetWidth;
                        iframe.style.height = `${currentWidth * ratio}px`;
                    };

                    // Prevent multiple listeners if message is sent again
                    if (iframe._seatMapResizeHandler) {
                        window.removeEventListener('resize', iframe._seatMapResizeHandler);
                    }
                    iframe._seatMapResizeHandler = updateHeight;

                    updateHeight();
                    window.addEventListener('resize', updateHeight);
                }
            }
        });
    }

    /**
     * @param {object} error
     * @return {{addons: boolean, discount: boolean}}
     */
    getAncillaryValidationErrors(error) {
        const errors = error?.response?.data?.errors;

        if (!errors || typeof errors !== 'object') {
            return {addons: false, discount: false};
        }

        const errorKeys = Object.keys(errors);
        const hasKeyOrNestedKey = (key, prefix) => key === prefix || key.startsWith(`${prefix}.`);

        return {
            addons: errorKeys.some(key => hasKeyOrNestedKey(key, 'addons')),
            discount: errorKeys.some(key => {
                return hasKeyOrNestedKey(key, 'discounts')
                    || [
                        'invalid_discount_code',
                        'discount_constraints_not_met',
                        'system_discount_is_better_than_code_discount',
                    ].includes(key);
            }),
        };
    }

    clearStateAtStartup() {
        return !this.isMixedCompositionEnabled();
    }

    isMixedCompositionEnabled() {
        return typeof this.globalState.isPublicMixedCompositionEnabled === 'function'
            && this.globalState.isPublicMixedCompositionEnabled();
    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'seat-type-select';
    }

    initView() {
        // Save order id from server
        this.globalState.setOrderId(
            getSingleInputByNameV3(this.domContainer, 'order_id')
        );

        this.initializeCustomerSpecialRequestsInput();

        // Init ticket count controls
        this.domContainer.querySelectorAll('.visitor_type_container .subtract-button').forEach(button => {
            button.addEventListener('click', () => {
                if (button.disabled === false) {
                    this.updateContainerCount(button.closest('.visitor_type_container'), -1)
                }
            })
        })

        this.domContainer.querySelectorAll('.visitor_type_container .add-button').forEach(button => {
            button.addEventListener('click', () => {
                if (button.disabled === false) {
                    this.updateContainerCount(button.closest('.visitor_type_container'), 1)
                }
            })
        })

        this.domContainer.querySelectorAll('.visitor_type_container input').forEach(input => {
            input.addEventListener('change', () => {
                this.cleanUpIntegerOnlyInput(input)
                // Check if it is a reserved seats section
                if (input.closest('.reserved-tickets')) {
                    this.handleReservedTicketInputCustomBehavior(input)
                } else {
                    this.handleGeneralAdmissionCustomBehavior(input)
                }

                this.applySharedInventoryFamilyBudget(input);
                this.handleTicketCountChange();
            })
            input.addEventListener('keyup', () => {
                this.cleanUpIntegerOnlyInput(input)
            })
        })

        // Init addon controls
        //// Setup addon select cards
        this.domContainer.querySelectorAll('.addon-container').forEach(showContainer => {
            const addonInput = showContainer.querySelector(`input[type="checkbox"]`);

            addonInput.addEventListener('change', () => {
                this.handleAddonToggle(showContainer);
                this.handleAddonChange();
            })

            // Trigger show selection
            showContainer.addEventListener('click', (e) => {
                const clickedOnInput = e.target === addonInput;
                const clickedOnCountControl =
                    e.target.classList.contains('count-container') || // If we clicked on the count container
                    (e.target.closest('.count-container') !== null) // Or a children of it

                // If we clicked on neither the input or the count controls...
                if (clickedOnInput === false && clickedOnCountControl === false) {
                    // ... Toggle checkbox and dispatch event
                    addonInput.checked = !addonInput.checked;
                    addonInput.dispatchEvent(new InputEvent('change'));
                }
            })
        })

        //// Init addon count controls
        this.domContainer.querySelectorAll('.addon-container .subtract-button').forEach(button => {
            button.addEventListener('click', () => {
                this.updateContainerCount(button.closest('.count-container'), -1)
            })
        })

        this.domContainer.querySelectorAll('.addon-container .add-button').forEach(button => {
            button.addEventListener('click', () => {
                this.updateContainerCount(button.closest('.count-container'), 1)
            })
        })

        this.domContainer.querySelectorAll('.addon-container input[type="text"]').forEach(input => {
            input.addEventListener('change', () => {
                this.cleanUpIntegerOnlyInput(input)
                this.handleAddonChange();
            })
            input.addEventListener('keyup', () => {
                this.cleanUpIntegerOnlyInput(input)
            })
        })

        // Setup handlers to update totals on change
        this.domContainer.querySelectorAll('*[data-update-totals-on-change]').forEach(input => {
            input.addEventListener('change', () => {
                // We put this inside a set timeout to make sure it executes AFTER
                // all other handlers update the state
                setTimeout(() => {
                    this.updateTotals();
                }, 0)
            })
        })

        this.totalPreviewComponent = this.getTotalPreviewComponent()

        this.initializeMixedCompositionControls();

        // Prefill data
        this.prefillData();
        this.prefillAddonData();

        if (this.shouldApplyAutoQuantityForSingleChoiceReservedSections()) {
            this.applyAutoQuantityForSingleChoiceReservedSections();
        }

        // First render
        this.domContainer.querySelectorAll('.visitor_type_container input').forEach(input => {
            // Trigger change event so all clean up and derived
            // values (remaining tickets for example) are set.
            input.dispatchEvent(new Event('change'));
        })
        this.handleTicketCountChange();
        this.updateTotals();


        // Override default next stage button
        this.domContainer.querySelector('.next-stage-button')
            .addEventListener(
                'click', e => {
                    e.stopPropagation();
                    e.preventDefault();

                    // Check for cancellation insurance
                    let hasCancellationInsurance = false;
                    this.globalState.addons.forEach(addon => {
                        // Cancellation insure addon id
                        if (addon.key === 'cancellation-insurance') {
                            hasCancellationInsurance = true;
                        }
                    })

                    if (this.shouldDisplayTosWarning()) {
                        // Display TOS warning
                        Swal.fire({
                            title: '',
                            html: `
                            By clicking continue I agree to the terms and conditions linked below and understand that all purchases are final and non-refundable.
                            <br>
                            <br>
                            <div class="text-center">
                                <a href="#" target="_blank">
                                    Terms and
                                    conditions.
                                </a>
                            </div>
                            `,
                            showCancelButton: true,
                            confirmButtonText: 'Continue',
                        }).then(result => {
                            if (result.isConfirmed) {
                                this.globalState.currentStage = this.getStageKey();
                                this.globalState.stageManager.nextStage();
                            }
                        })
                    } else {
                        // Go to next stage without displaying TOS warning
                        this.globalState.currentStage = this.getStageKey();
                        this.globalState.stageManager.nextStage();
                    }


                },
                {
                    capture: true,
                }
            )
    }

    display() {
        super.display();

        this.syncCustomerSpecialRequestsInput();
    }

    syncCustomerSpecialRequestsInput() {
        const customerSpecialRequestsInput = this.domContainer.querySelector(
            '[data-customer-special-requests-input]'
        );

        if (customerSpecialRequestsInput) {
            customerSpecialRequestsInput.value = this.globalState.customerSpecialRequests;
        }
    }

    initializeCustomerSpecialRequestsInput() {
        this.syncCustomerSpecialRequestsInput();

        const customerSpecialRequestsInput = this.domContainer.querySelector(
            '[data-customer-special-requests-input]'
        );
        if (customerSpecialRequestsInput) {
            customerSpecialRequestsInput.addEventListener('input', () => {
                this.globalState.setCustomerSpecialRequests(customerSpecialRequestsInput.value);
            });
        }
    }

    getTotalPreviewComponent() {
        return new TotalPreviewComponent(
            this.domContainer.querySelector('.totals-preview-wrapper'),
            this.globalState,
        );
    }

    shouldDisplayTosWarning() {
        return false;
    }

    handleTicketCountChange() {
        if (this.isMixedCompositionEnabled()) {
            this.saveGeneralAdmissionTicketCount();
            this.saveReserveTicketCount();
            this.updateCombinedNextButtonStatus();
        } else if (this.globalState.selectedSeatType === 'general-admission') {
            this.saveGeneralAdmissionTicketCount();
            if (this.globalState.generalAdmissionTicketCounts.length > 0) {
                this.enableNextStageButton();
            } else {
                this.disableNextStageButton();
            }
        } else {
            this.saveReserveTicketCount();

            let reservedTicketTotalCount = 0;

            this.globalState.reserveTicketCounts.forEach(ticket => {
                reservedTicketTotalCount += ticket.count
            })

            // Only enable next step if total tickets
            // matches number of selected seats
            if (reservedTicketTotalCount === this.globalState.selectedSeats.length) {
                this.enableNextStageButton();
            } else {
                this.disableNextStageButton();
            }
        }
        if (this.domContainer.querySelector('.addon-container[data-forced-minimum-type]')) {
            this.handleAddonChange();
        }
    }

    applyAutoQuantityForSingleChoiceReservedSections() {
        this.domContainer.querySelectorAll('.reserved-tickets .seat-group-section').forEach(section => {
            if (section.dataset.allSeatsRequired !== '1' && section.dataset.allSeatsRequired !== 'true') {
                return;
            }

            if (parseInt(section.dataset.purchasableTicketChoiceCount) !== 1) {
                return;
            }

            const ticketInputs = section.querySelectorAll('.seat-type-input');
            const selectedSeatCount = parseInt(section.dataset.maxAvailableSeats);

            if (ticketInputs.length !== 1 || isNaN(selectedSeatCount) || selectedSeatCount < 1) {
                return;
            }

            const input = ticketInputs[0];
            input.value = selectedSeatCount.toFixed(0);
            input.readOnly = true;
            input.setAttribute('aria-readonly', 'true');

            const remainingContainer = section.querySelector('.section-seats-remaining-container');
            if (remainingContainer) {
                remainingContainer.classList.add('d-none');
            }

            section.dataset.autoQuantityLocked = 'true';
            this.disableSectionQuantityButtons(section);
        })
    }

    shouldApplyAutoQuantityForSingleChoiceReservedSections() {
        return true;
    }

    /**
     * @param {HTMLElement} section
     */
    disableSectionQuantityButtons(section) {
        section.querySelectorAll('.add-button, .subtract-button').forEach(button => {
            button.disabled = true;
        })
    }


    saveGeneralAdmissionTicketCount() {
        /** @var {PublicTicketsFormDefs.SectionTicketCount[]} */
        const generalAdmissionTicketCount = [];

        // Get all general admission inputs
        this.domContainer.querySelectorAll('.general-admission-tickets .seat-type-input').forEach(input => {
            this.retrieveGeneralAdmissionTicketCount(input, generalAdmissionTicketCount);
        })

        this.globalState.setGeneralAdmissionTicketCounts(generalAdmissionTicketCount);
    }

    /**
     * Retrieves and processes the general admission ticket count from the provided input element.
     *
     * @param {HTMLInputElement} input
     * @param {PublicTicketsFormDefs.SectionTicketCount[]} generalAdmissionTicketCount
     * @return {void}
     */
    retrieveGeneralAdmissionTicketCount(input, generalAdmissionTicketCount) {
        const count = parseInt(input.value);

        // Skip empty entries
        if (isNaN(count)) {
            return;
        }

        generalAdmissionTicketCount.push({
            count,
            visitorTypeId: input.dataset.visitorTypeId,
            groupId: input.dataset.groupId,
            seatMapId: this.getSeatMapIdForTicketInput(input),
        })
    }

    saveReserveTicketCount() {
        /** @var {PublicTicketsFormDefs.SectionTicketCount[]} */
        const reservedTicketCounts = [];

        // Get all general admission inputs
        this.domContainer.querySelectorAll('.reserved-tickets .seat-type-input').forEach(input => {
            this.retrieveReservedTicketCount(input, reservedTicketCounts);
        })

        this.globalState.setReserveTicketCounts(reservedTicketCounts);
    }

    /**
     * Retrieves and processes the reserved ticket count from the provided input element.
     *
     * @param {HTMLInputElement} input
     * @param {PublicTicketsFormDefs.SectionTicketCount[]} reservedTicketCounts
     * @return {void}
     */
    retrieveReservedTicketCount(input, reservedTicketCounts) {
        const count = parseInt(input.value);

        // Skip empty entries
        if (isNaN(count)) {
            return;
        }

        // noinspection JSCheckFunctionSignatures
        reservedTicketCounts.push({
            count,
            visitorTypeId: input.dataset.visitorTypeId,
            groupId: input.dataset.groupId,
            seatMapId: this.getSeatMapIdForTicketInput(input),
        })
    }

    /**
     * @param {HTMLInputElement} input
     * @return {string}
     */
    getSeatMapIdForTicketInput(input) {
        const section = input.closest('.seat-group-section');

        return section ? section.dataset.seatMapId || '' : '';
    }

    handleAddonChange() {
        applyForcedMinimumAddons(this.domContainer);

        /** @var {PublicTicketsFormDefs.Addon[]} */
        const addons = [];

        // Get all general admission inputs
        this.domContainer.querySelectorAll('.addon-container input[type="checkbox"]:checked').forEach(input => {
            const key = String(input.value);

            const addon = {key};

            // Retrieve addon count if required
            if (input.dataset.withCount) {
                const count = parseInt(String(input.closest('.addon-container').querySelector('.addon-count-input').value));

                if (isNaN(count)) {
                    // Skip addon if count is not valid
                    return;
                }

                addon.count = count;
            }

            // This only applies to admin orders, it is validated server side
            // so no danger with having it here
            if (input.dataset.isFrozen) {
                addon.frozen = true;
                addon.id = input.dataset.addonId;
            }

            addons.push(addon);
        })

        this.globalState.setAddons(addons);
    }

    /**
     * @param {HTMLElement} container
     * @param {number} delta
     */
    updateContainerCount(container, delta) {
        const input = container.querySelector('input');

        let currentValue = parseInt(input.value);
        if (isNaN(currentValue)) {
            currentValue = 0;
        }

        const newValue = currentValue + delta;

        if (newValue <= 0) {
            input.value = ''
        } else {
            input.value = newValue.toFixed(0)
        }

        // Trigger change event on input
        input.dispatchEvent(new InputEvent('change'))
    }

    /**
     * @param {HTMLInputElement} input
     */
    cleanUpIntegerOnlyInput(input) {
        input.value = input.value.replace(/[^0-9]/g, '')
    }

    /**
     * @param {HTMLInputElement} input
     */
    handleGeneralAdmissionCustomBehavior(input) {
        // noinspection DuplicatedCode
        let inputValue = parseInt(input.value);

        if (isNaN(inputValue)) {
            inputValue = 0;
        }

        const parentSection = input.parentElement.closest('.seat-group-section');
        const requiredTickets = parseInt(parentSection.dataset.maxAvailableSeats);

        // First, make sure we are not going out of bounds in the section
        let sectionTotalTickets = 0;

        parentSection.querySelectorAll('.seat-type-input').forEach(input => {
            const ticketCount = parseInt(String(input.value));

            if (isNaN(ticketCount)) {
                return
            }

            sectionTotalTickets += ticketCount;
        })

        // Subtract from section total the current input, so we
        // can know what is the maximum allowed value remaining
        sectionTotalTickets -= inputValue;

        const maxRemaining = Math.max(0, requiredTickets - sectionTotalTickets);

        // If out of bounds, cap input value
        if (inputValue > maxRemaining) {
            inputValue = maxRemaining
        }

        if (inputValue) {
            // Update input with new max value
            input.value = inputValue.toFixed(0);
        } else {
            // Or clear the input
            input.value = ''
        }

        // Add back input value now that it has been capped
        sectionTotalTickets += inputValue;

        // And update section buttons
        const remainingTicketsRequiredInSection = requiredTickets - sectionTotalTickets;

        if (remainingTicketsRequiredInSection < 1) {
            // Disable all add buttons
            parentSection.querySelectorAll('.add-button').forEach(button => {
                button.disabled = true;
            })
        } else if (parentSection.dataset.autoQuantityLocked === 'true') {
            this.disableSectionQuantityButtons(parentSection);
        } else {
            // Enable all add buttons
            parentSection.querySelectorAll('.add-button').forEach(button => {
                button.disabled = false;
            })
        }
    }

    /**
     * @param {HTMLInputElement} input
     */
    handleReservedTicketInputCustomBehavior(input) {
        // noinspection DuplicatedCode
        let inputValue = parseInt(input.value);

        if (isNaN(inputValue)) {
            inputValue = 0;
        }

        const parentSection = input.parentElement.closest('.seat-group-section');
        const requiredTickets = parseInt(parentSection.dataset.maxAvailableSeats);

        // First, make sure we are not going out of bounds in the section
        let sectionTotalTickets = 0;

        parentSection.querySelectorAll('.seat-type-input').forEach(input => {
            const ticketCount = parseInt(String(input.value));

            if (isNaN(ticketCount)) {
                return
            }

            sectionTotalTickets += ticketCount;
        })

        // Subtract from section total the current input, so we
        // can know what is the maximum allowed value remaining
        sectionTotalTickets -= inputValue;

        const maxRemaining = Math.max(0, requiredTickets - sectionTotalTickets);

        // If out of bounds, cap input value
        if (inputValue > maxRemaining) {
            inputValue = maxRemaining
        }

        if (inputValue) {
            // Update input with new max value
            input.value = inputValue.toFixed(0);
        } else {
            // Or clear the input
            input.value = ''
        }

        // Add back input value now that it has been capped
        sectionTotalTickets += inputValue;

        // And update section header and buttons
        const remainingTicketsRequiredInSection = requiredTickets - sectionTotalTickets;
        parentSection.querySelector('.section-seats-remaining').innerHTML = remainingTicketsRequiredInSection.toFixed(0);

        if (remainingTicketsRequiredInSection < 1) {
            // Disable all add buttons
            parentSection.querySelectorAll('.add-button').forEach(button => {
                button.disabled = true;
            })
        } else {
            // Enable all add buttons
            parentSection.querySelectorAll('.add-button').forEach(button => {
                button.disabled = false;
            })
        }
    }


    /**
     * @param {HTMLElement} showContainer
     */
    handleAddonToggle(showContainer) {
        /** @var {HTMLInputElement} */
        const addonInput = showContainer.querySelector(`input`);

        // Toggle selected class
        showContainer.classList.toggle('selected', addonInput.checked);

        if (addonInput.dataset.withCount) {
            showContainer.querySelector('.count-control-container')
                .classList
                .toggle('d-none', addonInput.checked === false)
        }
    }

    updateTotals() {
        return this.totalPreviewComponent.updateTotalsFromServer();
    }

    /**
     * @param {object} error
     * @return {boolean}
     */
    recoverSharedVisitorTypeUnavailable(error) {
        const recovery = recoverSharedVisitorTypeUnavailable(error, this.globalState);

        if (!recovery) {
            return false;
        }

        displayLocalValidationError(document.body, 'DUMMY', recovery.message);
        this.globalState.recoveringFromCapacityConflict = true;
        this.domContainer.innerHTML = '';
        this.globalState.stageManager.clearStageInstance('seat-type-select');
        this.globalState.stageManager.goToStage('seat-type-select');

        return true;
    }

    getSharedInventoryFamilyInputs(token) {
        return Array.from(this.domContainer.querySelectorAll('.seat-type-input')).filter(input => {
            return input.dataset.sharedInventoryFamilyToken === token;
        });
    }

    getSharedInventoryFamilyMaximum(inputs) {
        const maxima = inputs
            .map(input => parseInt(input.dataset.sharedInventoryMaximum))
            .filter(maximum => !isNaN(maximum) && maximum >= 0);

        return maxima.length ? Math.min(...maxima) : null;
    }

    getSharedInventoryFamilyGaMaximum(inputs) {
        const maxima = inputs
            .filter(input => input.closest('.general-admission-tickets') && input.hasAttribute('data-shared-inventory-ga-maximum'))
            .map(input => parseInt(input.dataset.sharedInventoryGaMaximum))
            .filter(maximum => !isNaN(maximum) && maximum >= 0);

        return maxima.length ? Math.min(...maxima) : null;
    }

    getPositiveInputCount(input) {
        const count = parseInt(input.value);

        return isNaN(count) || count < 1 ? 0 : count;
    }

    applySharedInventoryFamilyBudget(input) {
        const token = input.dataset.sharedInventoryFamilyToken;
        const section = input.closest('.seat-group-section');
        const sectionFamilyTokens = new Set(Array.from(section?.querySelectorAll('.seat-type-input') || [])
            .map(sectionInput => sectionInput.dataset.sharedInventoryFamilyToken)
            .filter(sectionToken => !!sectionToken));
        if (!token) {
            sectionFamilyTokens.forEach(sectionToken => this.updateSharedInventoryFamilyAddButtons(sectionToken));

            return;
        }

        const inputs = this.getSharedInventoryFamilyInputs(token);
        const maximum = this.getSharedInventoryFamilyMaximum(inputs);
        const gaMaximum = this.getSharedInventoryFamilyGaMaximum(inputs);

        const requestedCount = this.getPositiveInputCount(input);
        let inputCount = requestedCount;
        const otherCount = inputs
            .filter(familyInput => familyInput !== input)
            .reduce((total, familyInput) => total + this.getPositiveInputCount(familyInput), 0);
        let allowedCount = maximum === null ? Infinity : Math.max(0, maximum - otherCount);
        if (gaMaximum !== null && input.closest('.general-admission-tickets')) {
            const otherGaCount = inputs
                .filter(familyInput => familyInput !== input && familyInput.closest('.general-admission-tickets'))
                .reduce((total, familyInput) => total + this.getPositiveInputCount(familyInput), 0);
            allowedCount = Math.min(allowedCount, Math.max(0, gaMaximum - otherGaCount));
        }

        if (inputCount > allowedCount) {
            inputCount = allowedCount;
            input.value = inputCount ? String(inputCount) : '';
        }

        if (inputCount !== requestedCount) {
            const sectionMaximum = parseInt(section?.dataset.maxAvailableSeats);
            const remaining = !isNaN(sectionMaximum) && section
                ? sectionMaximum - Array.from(section.querySelectorAll('.seat-type-input'))
                .reduce((sum, sectionInput) => sum + this.getPositiveInputCount(sectionInput), 0)
                : null;
            const remainingDisplay = section?.querySelector('.section-seats-remaining');
            if (remainingDisplay && remaining !== null) {
                remainingDisplay.textContent = String(remaining);
            }
        }

        sectionFamilyTokens.add(token);
        sectionFamilyTokens.forEach(sectionToken => this.updateSharedInventoryFamilyAddButtons(sectionToken));
    }

    updateSharedInventoryFamilyAddButtons(token) {
        const inputs = this.getSharedInventoryFamilyInputs(token);
        const maximum = this.getSharedInventoryFamilyMaximum(inputs);
        const gaMaximum = this.getSharedInventoryFamilyGaMaximum(inputs);
        if (maximum === null && gaMaximum === null) {
            return;
        }

        const total = inputs.reduce((sum, input) => sum + this.getPositiveInputCount(input), 0);
        const gaTotal = inputs.filter(input => input.closest('.general-admission-tickets'))
            .reduce((sum, input) => sum + this.getPositiveInputCount(input), 0);
        const familyIsFull = maximum !== null && total >= maximum;
        const gaIsFull = gaMaximum !== null && gaTotal >= gaMaximum;

        inputs.forEach(input => {
            const section = input.closest('.seat-group-section');
            const sectionMaximum = parseInt(section?.dataset.maxAvailableSeats);
            const sectionTotal = Array.from(section?.querySelectorAll('.seat-type-input') || [])
                .reduce((sum, sectionInput) => sum + this.getPositiveInputCount(sectionInput), 0);
            const sectionIsFull = !isNaN(sectionMaximum) && sectionTotal >= sectionMaximum;

            input.closest('.visitor_type_container')?.querySelectorAll('.add-button').forEach(button => {
                button.disabled = familyIsFull || sectionIsFull ||
                    (gaIsFull && !!input.closest('.general-admission-tickets')) || section?.dataset.autoQuantityLocked === 'true';
            });
        });
    }

    initializeMixedCompositionControls() {
        if (!this.isMixedCompositionEnabled()) {
            return;
        }

        const toggle = this.domContainer.querySelector('.mixed-general-admission-toggle');
        if (toggle) {
            toggle.addEventListener('change', () => this.handleGeneralAdmissionToggle(toggle));
        }

        const addChangeButton = this.domContainer.querySelector('.add-change-reserved-button');
        if (addChangeButton) {
            addChangeButton.textContent = `ADD/CHANGE ${this.getReservedEntryLabel()}`;
            addChangeButton.addEventListener('click', event => {
                event.preventDefault();
                event.stopPropagation();

                this.globalState.setSeatType('reserved');
                this.globalState.stageManager.goToStage('seat-select');
            });
        }

        const generalAdmissionLabel = this.domContainer.querySelector('.mixed-general-admission-label');
        if (generalAdmissionLabel) {
            generalAdmissionLabel.textContent = this.getGeneralAdmissionEntryLabel();
        }

        this.syncGeneralAdmissionSection();
    }

    getReservedEntryLabel() {
        const override = String(this.globalState.parentEvent?.reservedEntryLabel || '').trim();

        return override || this.globalState.effectiveReservedEntryLabel || 'RESERVED TICKETS';
    }

    getGeneralAdmissionEntryLabel() {
        const override = String(this.globalState.parentEvent?.generalAdmissionEntryLabel || '').trim();

        return override || 'GENERAL ADMISSION';
    }

    handleGeneralAdmissionToggle(toggle) {
        if (toggle.checked) {
            this.globalState.setGeneralAdmissionEnabled(true);
        } else {
            const generalAdmissionInputs = Array.from(this.domContainer.querySelectorAll('.general-admission-tickets .seat-type-input'));
            generalAdmissionInputs.forEach(input => {
                input.value = '';
            });
            generalAdmissionInputs.forEach(input => {
                this.handleGeneralAdmissionCustomBehavior(input);
                this.applySharedInventoryFamilyBudget(input);
            });
            this.globalState.setGeneralAdmissionTicketCounts([]);
            this.globalState.setGeneralAdmissionEnabled(false);
        }

        this.syncGeneralAdmissionSection();
        this.handleTicketCountChange();
        this.updateTotals();
    }

    syncGeneralAdmissionSection() {
        const section = this.domContainer.querySelector('.mixed-general-admission-section');
        if (!section) {
            return;
        }

        const toggle = section.querySelector('.mixed-general-admission-toggle');
        const content = section.querySelector('.mixed-general-admission-content');
        const enabled = this.globalState.generalAdmissionEnabled === true;

        if (toggle) {
            toggle.checked = enabled;
            toggle.setAttribute('aria-expanded', enabled ? 'true' : 'false');
        }

        if (content) {
            content.classList.toggle('d-none', enabled === false);
        }
    }

    updateCombinedNextButtonStatus() {
        if (this.globalState.stageManager.isCombinedTicketSelectionReady()) {
            this.enableNextStageButton();
        } else {
            this.disableNextStageButton();
        }
    }

    /**
     * Prefills server response HTML with our data in memory.
     * Public instance always requires the user to start from scratch
     * while admin instance allows him to preserve his changes.
     */
    prefillData() {
        if (!this.isMixedCompositionEnabled()) {
            // Do nothing, only admin instance preserves state in the ordinary flow.
            return;
        }

        const selections = (this.globalState.generalAdmissionTicketCounts || [])
            .concat(this.globalState.reserveTicketCounts || []);

        selections.forEach(selection => {
            const input = this.getTicketCountInputForSelection(selection);

            if (input) {
                input.value = String(selection.count);
            }
        });

        if (
            this.globalState.hasPositiveGeneralAdmissionTicketCounts()
            || this.globalState.selectedSeatType === 'general-admission'
        ) {
            this.globalState.setGeneralAdmissionEnabled(true);
        }

        this.syncGeneralAdmissionSection();
    }

    prefillAddonData() {
        if (!this.isMixedCompositionEnabled()) {
            return;
        }

        const selectedAddons = new Map(
            (this.globalState.addons || [])
                .filter(addon => addon && addon.key !== undefined)
                .map(addon => [String(addon.key), addon])
        );

        this.domContainer.querySelectorAll('.addon-container').forEach(showContainer => {
            const addonInput = showContainer.querySelector('input[type="checkbox"]');

            if (!addonInput) {
                return;
            }

            const addon = selectedAddons.get(String(showContainer.dataset.addonKey || addonInput.value));
            addonInput.checked = addon !== undefined;

            if (addon !== undefined && addon.count !== undefined) {
                const countInput = showContainer.querySelector('.addon-count-input');

                if (countInput) {
                    countInput.value = String(addon.count);
                }
            }

            this.handleAddonToggle(showContainer);
        });
    }

    /**
     * @param {PublicTicketsFormDefs.SectionTicketCount} selection
     * @return {HTMLInputElement|null}
     */
    getTicketCountInputForSelection(selection) {
        const matchingInputs = Array.from(this.domContainer.querySelectorAll(
            `input[data-group-id="${selection.groupId}"][data-visitor-type-id="${selection.visitorTypeId}"]`
        ));

        if (matchingInputs.length < 2 || !selection.seatMapId) {
            return matchingInputs[0] || null;
        }

        return matchingInputs.find(input => {
            const section = input.closest('.seat-group-section');

            return section && section.dataset.seatMapId === selection.seatMapId;
        }) || null;
    }
}

export {TicketTypeSelectStage};
